import { FilesSelector } from 'components/FilesSelector/FilesSelector';
import { useReadFilesAsBase64 } from 'components/FilesSelector/hooks/useReadFilesAsBase64';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import { IconWarning, Informer, Select } from 'uikit';

import type React from 'react';
import { useMemo, useCallback, useState } from 'react';

import { useUerWorkOrderChangeAllowedSeasons } from '../hooks/useUerWorkOrderChangeAllowedSeasons';

import { useUerAddWorkOrderChangeFromXml } from './hooks/useUerAddWorkOrderChangeFromXml';

interface IAddWorkOrderChangeFromXmlModal {
    workOrderChangeId: number;
    contractId: number;
    onClose: (isSuccess?: boolean, hasChanges?: boolean) => void;
}

const style = { width: 520 };
const bodyStyle = { display: 'flex', flexDirection: 'column', margin: 24 } as React.CSSProperties;
const marginStyle = { marginBottom: 16 };

const accept = ['.xml'];

export const AddWorkOrderChangeFromXmlModal: React.FC<IAddWorkOrderChangeFromXmlModal> = (props) => {
    const { contractId, workOrderChangeId, onClose } = props;

    const [constructionTypes] = useContractConstructionTypes(contractId, ProjectMethodologyEnum.Uer);

    const constructionTypesItems = useMemo(
        () => constructionTypes?.map((c) => ({ id: c.id, label: c.shortName })),
        [constructionTypes],
    );

    const { uerWorkOrderChangeAllowedSeasonsData } = useUerWorkOrderChangeAllowedSeasons(workOrderChangeId);

    const seasonsItems = useMemo(
        () =>
            uerWorkOrderChangeAllowedSeasonsData?.uerWorkOrderChangeAllowedSeasons?.map((s) => ({
                id: s.id,
                label: s.year,
            })) || [],
        [uerWorkOrderChangeAllowedSeasonsData?.uerWorkOrderChangeAllowedSeasons],
    );

    const [files, setFiles] = useState<File[] | undefined>([]);
    const base64Files = useReadFilesAsBase64(files);

    const [constructionType, setConstructionType] = useState<{ id: number; label: string }>();

    const [season, setSeason] = useState<{ id: number; label: string }>();

    const btnConfirmDisabled = useMemo(
        () => !base64Files || !constructionType?.id || !season?.id,
        [base64Files, constructionType?.id, season?.id],
    );

    const onSuccess = useCallback(() => {
        onClose(true);
    }, [onClose]);

    const onCloseLocal = useCallback(() => {
        onClose(false, !!base64Files || !!constructionType?.id || !!season?.id);
    }, [base64Files, constructionType?.id, onClose, season?.id]);

    const { status, makeRequest } = useUerAddWorkOrderChangeFromXml({ workOrderChangeId, onSuccess });

    const onConstructionTypeChange = useCallback(({ value }) => {
        setConstructionType(value);
    }, []);

    const onSeasonChange = useCallback(({ value }) => {
        setSeason(value);
    }, []);

    const onFilesChange = useCallback((newFiles?: File[]) => {
        setFiles(newFiles);
    }, []);

    const onConfirm = useCallback(() => {
        if (base64Files && constructionType?.id && season?.id) {
            makeRequest(constructionType?.id, season?.id, base64Files);
        }
    }, [base64Files, constructionType?.id, season?.id, makeRequest]);

    return (
        <ModalDialog
            title="добавление ведомости объемов работ"
            onConfirm={onConfirm}
            onClose={onCloseLocal}
            btnConfirmDisabled={btnConfirmDisabled}
            isCommit={status === RequestStatus.FETCHING}
            style={style}
            btnConfirmLabel="Добавить"
            isOnClickOutsideDisable
            withTitleCloseButton
            hasOverlay
            isOpen
        >
            <div style={bodyStyle}>
                <Informer
                    view="filled"
                    status="warning"
                    label="Загружаемый файл ВОР в формате xml будет относиться к выбранному виду строительства и сезону"
                    icon={IconWarning}
                    size="s"
                    style={marginStyle}
                />
                <Select
                    items={constructionTypesItems}
                    value={constructionType}
                    onChange={onConstructionTypeChange}
                    labelPosition="left"
                    label="Вид строительства"
                    size="s"
                    placeholder="Выберите"
                    style={marginStyle}
                />
                <Select
                    items={seasonsItems}
                    value={season}
                    onChange={onSeasonChange}
                    labelPosition="left"
                    label="Сезон"
                    size="s"
                    placeholder="Выберите"
                    style={marginStyle}
                />
                <FilesSelector
                    files={files}
                    accept={accept}
                    isLoading={status === RequestStatus.FETCHING}
                    typeName={accept[0]}
                    manualOperationName="выберите вручную"
                    onFilesChange={onFilesChange}
                    isExternalFilesState
                    multiple
                />
            </div>
        </ModalDialog>
    );
};

AddWorkOrderChangeFromXmlModal.displayName = 'AddWorkOrderChangeFromXmlModal';
